/**
 * A minimal history-based router (no dependency). The admin Worker serves the
 * data-free SPA shell for every path (`not_found_handling: single-page-application`),
 * so deep links like `/rooms/:id` survive a hard refresh. Routes:
 *
 *   /login            — the only public screen
 *   /rooms            — tenant picker (no tenant) or the rooms list (?tenant=…)
 *   /rooms/new        — create room
 *   /rooms/:id        — room detail
 *   /alerts           — the tenant's active health alerts (acknowledge / re-check)
 *
 * Navigation is via the exported `navigate()` and `useLocation()` — internal links
 * are plain Oxygen Buttons/TextLinks calling `navigate`, so there's no anchor
 * interception to maintain.
 */

import { useEffect, useState } from 'react';

export type RouteName = 'login' | 'rooms' | 'create' | 'detail' | 'alerts' | 'calendars' | 'unknown';

export interface Route {
    name: RouteName;
    /** Captured path param for /rooms/:id. */
    roomId?: string;
    query: URLSearchParams;
    path: string;
}

/** A tiny pub/sub so every `useLocation` re-renders on navigation. */
const listeners = new Set<() => void>();

function emit(): void {
    listeners.forEach(fn => fn());
}

/** Navigate to a same-origin path (pushState by default, or replaceState). */
export function navigate(to: string, replace = false): void {
    if (replace) {
        history.replaceState(null, '', to);
    } else {
        history.pushState(null, '', to);
    }
    emit();
}

/** Only accept same-origin relative paths for `?next` (open-redirect guard). */
export function safeNext(next: string | null): string {
    if (!next || !next.startsWith('/') || next.startsWith('//') || next.startsWith('/\\')) {
        return '/rooms';
    }

    return next;
}

/** The current `pathname + search` (for building a `?next`). */
export function currentHref(): string {
    return location.pathname + location.search;
}

function parse(): Route {
    const path = location.pathname === '/' ? '/rooms' : location.pathname;
    const query = new URLSearchParams(location.search);

    if (path === '/login') {
        return { name: 'login', query, path };
    }
    if (path === '/rooms' || path === '/rooms/') {
        return { name: 'rooms', query, path };
    }
    if (path === '/rooms/new') {
        return { name: 'create', query, path };
    }
    if (path === '/alerts' || path === '/alerts/') {
        return { name: 'alerts', query, path };
    }
    if (path === '/calendars' || path === '/calendars/') {
        return { name: 'calendars', query, path };
    }

    const detail = /^\/rooms\/([^/]+)\/?$/.exec(path);

    if (detail) {
        return { name: 'detail', roomId: decodeURIComponent(detail[1]), query, path };
    }

    return { name: 'unknown', query, path };
}

/** Subscribe a component to the current route; re-renders on navigation. */
export function useLocation(): Route {
    const [route, setRoute] = useState<Route>(parse);

    useEffect(() => {
        const update = (): void => setRoute(parse());

        listeners.add(update);
        window.addEventListener('popstate', update);

        return () => {
            listeners.delete(update);
            window.removeEventListener('popstate', update);
        };
    }, []);

    return route;
}
